import React, { useCallback, useState, useEffect, useRef } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import Web3 from 'web3';

import Link from '../common/Link/Link';
import Button from '../common/Button/Button';
import { RoutingPaths } from '../../paths';
import { NetworkID } from '../../constants/index';
import { useUserContext } from '../../contexts/UserContext';

import { currencyFormatter, truncateKey } from '../../_helpers';
import { getAdamantTokenDollarPrice } from '../../api/Multichain';

import { ReactComponent as AdamantLogo } from './logo.svg';
import styles from './Navbar.module.scss';

const quickswapUrl =
  'https://quickswap.exchange/#/swap?inputCurrency=0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619&outputCurrency=0xc3fdbadc7c795ef1d6ba111e06ff8f16a20ea539';
const sushiUrl =
  'https://app.sushi.com/swap?inputCurrency=0x82aF49447D8a07e3bd95BD0d56f35241523fBab1&outputCurrency=0x09ad12552ec45f82bE90B38dFE7b06332A680864';
const cronaswapUrl =
  'https://app.cronaswap.org/swap?inputCurrency=0x5C7F8A570d578ED84E63fdFA7b1eE72dEae1AE23&outputCurrency=0x09ad12552ec45f82bE90B38dFE7b06332A680864';

interface NavbarProps {
  account: string;
  onConnectWallet: () => void;
  web3?: Web3;
}

const Navbar: React.VFC<NavbarProps> = ({ account, onConnectWallet, web3 }) => {
  const [isLinksDropdownOpen, setIsLinksDropdownOpen] = useState(false);
  const linksDropdownRef = useRef<HTMLDivElement>(null);
  const linksDropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [isOptionsDropdownOpen, setIsOptionsDropdownOpen] = useState(false);
  const optionsDropdownNavbarRef = useRef<HTMLDivElement>(null);
  const optionsDropdownBottomBarRef = useRef<HTMLDivElement>(null);
  const startInvestingButtonRef = useRef<HTMLButtonElement>(null);
  const { push } = useHistory();
  const location = useLocation();
  const [gasPrice, setGasPrice] = useState<number>();
  const { isWalletConnected } = useUserContext();

  const isLandingPage = location.pathname === RoutingPaths.LANDING_PAGE;

  const toggleIsLinksDropdownOpen = useCallback(() => {
    setIsLinksDropdownOpen(!isLinksDropdownOpen);
  }, [isLinksDropdownOpen]);

  const handleClickOutsideLinks = useCallback((_target: EventTarget | null) => {
    if (
      linksDropdownRef.current &&
      linksDropdownButtonRef.current &&
      startInvestingButtonRef.current &&
      // @ts-ignore
      !linksDropdownRef.current.contains(_target) &&
      // @ts-ignore
      !linksDropdownButtonRef.current.contains(_target) &&
      // @ts-ignore
      !startInvestingButtonRef.current.contains(_target)
    ) {
      setIsLinksDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', (ev) => handleClickOutsideLinks(ev.target));

    return document.removeEventListener('mousedown', (ev) => handleClickOutsideLinks(ev.target));
  }, [handleClickOutsideLinks]);

  const createOnNavLinkClick = useCallback(
    (path: string) => () => {
      setIsLinksDropdownOpen(false);
      push(path);
    },
    [push],
  );

  const toggleIsOptionsDropdownOpen = useCallback(() => {
    setIsOptionsDropdownOpen(!isOptionsDropdownOpen);
  }, [isOptionsDropdownOpen]);

  const handleClickOutsideOptions = useCallback(
    (_target: EventTarget | null) => {
      if (
        optionsDropdownNavbarRef.current &&
        optionsDropdownBottomBarRef.current &&
        // @ts-ignore
        !optionsDropdownNavbarRef.current.contains(_target) &&
        // @ts-ignore
        !optionsDropdownBottomBarRef.current.contains(_target)
      ) {
        setIsOptionsDropdownOpen(false);
      }
    },
    [optionsDropdownNavbarRef],
  );

  useEffect(() => {
    document.addEventListener('mousedown', (ev) => handleClickOutsideOptions(ev.target));

    return document.removeEventListener('mousedown', (ev) => handleClickOutsideOptions(ev.target));
  }, [handleClickOutsideOptions]);

  useEffect(() => {
    if (web3 && web3.eth) {
      web3.eth.getGasPrice().then((price: string) => {
        const priceInGwei = Math.round(Number(price) / 10 ** 9);
        setGasPrice(priceInGwei);
      });
    }
  });

  let exchangeUrl = quickswapUrl;

  if (window.networkId === NetworkID.ARBITRUM_NETWORK_ID) {
    exchangeUrl = sushiUrl;
  } else if (window.networkId === NetworkID.CRONOS_NETWORK_ID) {
    exchangeUrl = cronaswapUrl;
  }

  interface NavigationLinksProps {
    className?: string;
  }

  const NavigationLinks = useCallback(
    ({ className }: NavigationLinksProps) => (
      <ul className={classnames(styles.linksList, className)}>
        <li>
          <NavLink
            className={styles.navLink}
            activeClassName={styles.activeNavLink}
            to={RoutingPaths.VAULTS}
            onClick={createOnNavLinkClick(RoutingPaths.VAULTS)}
          >
            Vaults
          </NavLink>
        </li>
        <li>
          <NavLink
            className={styles.navLink}
            activeClassName={styles.activeNavLink}
            to={RoutingPaths.STAKE_ADDY}
            onClick={createOnNavLinkClick(RoutingPaths.STAKE_ADDY)}
          >
            Stake + Vest
          </NavLink>
        </li>
      </ul>
    ),
    [createOnNavLinkClick],
  );

  interface TokenPriceProps {
    className?: string;
  }

  const TokenPrice = useCallback(
    ({ className }: TokenPriceProps) => (
      <Link href={exchangeUrl} className={classnames(styles.tokenPriceWrapper, className)} newTab>
        <img src="/icons/AdamantTokenIcon.svg" alt="" />
        <span className={styles.tokenPrice}>
          {currencyFormatter(getAdamantTokenDollarPrice(), {
            maximumSignificantDigits:
              window.networkId === NetworkID.CRONOS_NETWORK_ID ? 5 : undefined,
          })}
        </span>
      </Link>
    ),
    [exchangeUrl],
  );

  interface GasPriceProps {
    className?: string;
  }

  const GasPrice = useCallback(
    ({ className }: GasPriceProps) => (
      <div className={classnames(styles.gasPriceWrapper, className)}>
        <img src="/icons/GasIcon.svg" alt="" />
        <span className={styles.gasPrice}>{gasPrice || '…'}</span>
      </div>
    ),
    [gasPrice],
  );

  const createHandleOptionsButtonClick = useCallback(
    (url: string) => () => {
      window.open(url, '_blank', 'noopener noreferrer');
      setIsOptionsDropdownOpen(false);
    },
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const OptionsDropdownButton = useCallback(
    React.forwardRef<HTMLDivElement, { className?: string }>(({ className }, ref) => (
      <div ref={ref}>
        <Button
          className={classnames(styles.dropdownButton, styles.ellipsisButton, className)}
          onClick={toggleIsOptionsDropdownOpen}
        >
          <img
            src="/icons/EllipsisIcon.svg"
            alt=""
            className={classnames(
              styles.optionsDropdownButton,
              isOptionsDropdownOpen && styles.active,
            )}
          />
        </Button>
        {isOptionsDropdownOpen && (
          <div className={classnames(styles.dropdown, styles.optionsDropdown)}>
            <Button
              onClick={createHandleOptionsButtonClick(
                'https://adamantfinance.gitbook.io/adamant-finance/',
              )}
            >
              Documentation
            </Button>
            <Button onClick={createHandleOptionsButtonClick('https://discord.gg/69ureKKYz2')}>
              Discord
            </Button>
          </div>
        )}
      </div>
    )),
    [createHandleOptionsButtonClick, isOptionsDropdownOpen, toggleIsOptionsDropdownOpen],
  );

  const handleStartInvestingClick = useCallback(() => {
    setIsLinksDropdownOpen(false);
    push(RoutingPaths.VAULTS);
  }, [push]);

  return (
    <>
      <nav className={styles.container}>
        <NavLink className={styles.logoWrapper} to={RoutingPaths.LANDING_PAGE}>
          <AdamantLogo />
        </NavLink>

        <div className={styles.contentRight}>
          <NavigationLinks />

          {!isLandingPage && <TokenPrice className={styles.inNavbar} />}

          {!isLandingPage && <GasPrice className={styles.inNavbar} />}

          {isLandingPage ? (
            <Button
              onClick={handleStartInvestingClick}
              color="primary"
              className={classnames(styles.ctaButton, isLinksDropdownOpen && styles.visible)}
              ref={startInvestingButtonRef}
            >
              Start Investing
            </Button>
          ) : (
            <Button
              kind="outlined"
              color="primary"
              className={classnames(styles.connectWalletButton)}
              onClick={onConnectWallet}
            >
              {isWalletConnected ? truncateKey(account) : 'Connect Wallet'}
            </Button>
          )}

          <Button
            className={classnames(styles.dropdownButton, styles.hamburgerMenuButton)}
            onClick={toggleIsLinksDropdownOpen}
            ref={linksDropdownButtonRef}
          >
            <img
              src={isLinksDropdownOpen ? '/icons/CloseIcon.svg' : '/icons/HamburgerIcon.svg'}
              alt=""
            />
          </Button>

          {isLinksDropdownOpen && (
            <div
              className={classnames(styles.dropdown, styles.linksDropdown)}
              ref={linksDropdownRef}
            >
              <NavigationLinks className={styles.inDropdown} />
            </div>
          )}

          {!isLandingPage && (
            <OptionsDropdownButton ref={optionsDropdownNavbarRef} className={styles.inNavbar} />
          )}
        </div>
      </nav>

      {!isLandingPage && (
        <div className={styles.bottomBar}>
          <div className={styles.contentLeft}>
            <TokenPrice />
            <GasPrice />
          </div>
          <OptionsDropdownButton ref={optionsDropdownBottomBarRef} />
        </div>
      )}
    </>
  );
};

export default Navbar;
